import { createNlsMiddleware, userstate } from 'lcc-state';
import { DateTime } from 'luxon';

import { DNode } from '@dojo/framework/core/interfaces';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import Button from '@dojo/widgets/button';
import { Instructor, RosterMember, rostersvc } from '@holmescorp/class-roster';
import { Editor } from '@holmescorp/editor-widget';

import { css } from '../css/ati.m.css';
import { asyncLoad } from '../support/asyncLoad';
import { Message, Question } from '../support/conversation-svc';

interface AskTheInstructorICache {
	conversation: Question[];
	instructors: RosterMember[];
	isReplying: boolean | undefined;
	isSaving: boolean | undefined;
	threadId: number | undefined;
	message: string;
}

const factory = create({
	icache: createICacheMiddleware<AskTheInstructorICache>(),
	nls: createNlsMiddleware('ati'),
	userstate,
	theme
});

export const AskTheInstructor = factory(({ middleware: { icache, nls, userstate, theme } }) => {
	const { get, path } = userstate;
	const user_id = get(path('user_id'));

	const { isPlaceholder } = nls();
	if (isPlaceholder) {
		return null;
	}

	const themedCss = theme.classes(css);

	const saveContent = async (content: string | undefined) => {
		if (content) {
			const msgData: Message = {
				reply: icache.get('isReplying') || false,
				author_id: user_id,
				to_instructor: true,
				message: content
			};

			const threadId = icache.get('threadId');
			if (threadId) {
				msgData.root_id = threadId;
			}

			await asyncLoad({
				method: 'update_conversations',
				sessionid: window.hcstatic.course_session?.pk as number,
				site: window.hcstatic.sitealias,
				product: window.hcstatic.product?.toLowerCase(),
				data: msgData
			});

			const updatedConversations = await getMessages();
			icache.set('conversation', updatedConversations);
			icache.set('message', '');
			icache.set('isSaving', false);
		}
	};

	const getMessages = async (): Promise<Question[]> => {
		const conversations = await asyncLoad({
			method: 'conversations',
			sessionid: window.hcstatic.course_session?.pk as number,
			site: window.hcstatic.sitealias,
			product: window.hcstatic.product?.toLowerCase(),
			author_id: user_id,
			select: {
				sort: [
					{
						attribute: 'latest_reply_posted',
						descending: true
					}
				]
			}
		});

		if (conversations.length > 0) {
			icache.set('isReplying', true);
			icache.set('threadId', conversations[0].root_id);
		}

		return conversations;
	};

	const currentMessages = icache.getOrSet('conversation', getMessages);

	const generateMessageContent = (message: Question) => {
		const pubdate = DateTime.fromSQL(message.posted).toFormat('ff');
		const author = message.fullname || '';

		return v('div', { classes: themedCss.messageItem, xqa: 'message-item' }, [
			v('div', { classes: themedCss.messageMetadata }, [
				v('span', { classes: themedCss.messageAuthor, innerHTML: author }),
				v('span', { classes: themedCss.messagePostDate, innerHTML: pubdate })
			]),
			v('div', { classes: themedCss.messageText }, [v('p', { innerHTML: message.message })])
		]);
	};

	const allMessages = (header: DNode) => {
		const messageContent = [header];
		const conversation = currentMessages || icache.get('conversation');
		const messages: DNode[] = [];

		conversation?.forEach((message: Question) => {
			message.replies.forEach((reply: Question) => {
				messages.push(generateMessageContent(reply));
			});

			messages.push(generateMessageContent(message));
		});

		messages?.forEach((message: DNode) => {
			messageContent.push(message);
		});

		return messageContent;
	};

	const instructors = icache.getOrSet('instructors', async () => {
		const allMembers = await rostersvc.roster_members({
			session_id: window.hcstatic.course_session?.pk
		});

		return allMembers?.filter((member: RosterMember) => {
			return member.is_instructor;
		});
	});

	return v('div', { classes: [themedCss.atiContainer] }, [
		v('div', { classes: [themedCss.leftPanel, 'col-sm-8', 'col-md-7'] }, [
			v('div', { classes: [themedCss.messages] }, allMessages(v('h2', { innerHTML: 'Messages' }))),
			v('div', { classes: [themedCss.messageReply] }, [
				v('h2', { innerHTML: 'Ask' }),
				w(Editor, {
					content: icache.getOrSet('message', ''),
					onContent: (data: string) => {
						icache.set('message', data, false);
					}
				}),
				w(
					Button,
					{
						onClick: () => {
							if (!icache.get('isSaving')) {
								icache.set('isSaving', true);
								saveContent(icache.get('message'));
							}
						}
					},
					['Save']
				)
			])
		]),
		v('div', { classes: [themedCss.rightPanel, 'col-sm-4', 'col-md-4', 'col-md-offset-1', 'col-lg-3'] }, [
			v('h2', { innerHTML: 'Your Instructor' }),
			instructors ? w(Instructor, { instructors: instructors }) : null
		])
	]);
});
